import queryString from 'qs'
import pickBy from 'lodash/pickBy'
import isEmpty from 'lodash/isEmpty'
import Logger from '#src/services/logger'
import { PathLayouts } from '#src/typings'
import {
  PathFlowFragment,
  PathFlowPagesConnectionFragment,
  PathFlowOnCompleteRedirectLocation,
  PathFlowOnCompleteConversationWithLeadFragment,
  PathFlowPageMetadataFragment,
  NumericPathFlowQuestionUnitType,
  CreatePathFlowInput,
  GenericModalPageMetadataFragment
} from '#src/generated/graphql'

/**
 * sourceKey: a reference to where the path was created from
 */
interface CreatePathProps {
  sourceKey: string
  pathIntent: Tracking.PathIntent
  prefillData?: CreatePathFlowInput
  onboardingAsDefault?: boolean
}

/**
 * Remove all falsy values from prefill data and return null if no truthy values present
 * This allows us to construct a prefillData object without worrying if the value is present
 */
export function cleanPrefillData(prefillData?: CreatePathFlowInput) {
  const cleanedPrefillData = pickBy(prefillData, val => !!val)
  return isEmpty(cleanedPrefillData) ? null : cleanedPrefillData
}

/**
 * This method is exists to have a centralized location where our onboarding path is referenced
 */
export function getOnboardingPathSlug() {
  return 'native-onboarding-v1'
}

/**
 * This method is exists to have a centralized location where our organic onboarding path is referenced
 */
export function getOrganicOnboardingPathSlug() {
  return 'organic-onboarding-v1'
}

/**
 * Derive the path layout enum given a string
 */
export function derivePathLayout(maybePathLayout?: string) {
  switch (maybePathLayout) {
    case 'bubba':
      return PathLayouts.BUBBA

    case 'woods':
      return PathLayouts.WOODS

    case 'brooks':
      return PathLayouts.BROOKS

    case 'rory':
      return PathLayouts.RORY

    case 'default':
    default:
      return PathLayouts.DEFAULT
  }
}

/**
 * Given a category slug, create a url where they can create a new path from
 */
interface CreateFromCategoryProps extends CreatePathProps {
  maybeCategorySlug?: string | null
}

export function generateCreateUrlFromCategory({
  sourceKey,
  pathIntent,
  prefillData,
  maybeCategorySlug,
  onboardingAsDefault = true
}: CreateFromCategoryProps) {
  const cleanedPrefillData = cleanPrefillData(prefillData)
  const prefillDataQueryString = cleanedPrefillData
    ? `?${queryString.stringify(
        cleanedPrefillData
      )}&sourceKey=${sourceKey}&pathIntent=${pathIntent}`
    : `?sourceKey=${sourceKey}&pathIntent=${pathIntent}`

  if (maybeCategorySlug) {
    return `/requests/create-from-category/${maybeCategorySlug}${prefillDataQueryString}`
  }

  if (onboardingAsDefault) {
    return generateCreateUrl({
      maybePathSlug: getOnboardingPathSlug(),
      sourceKey,
      prefillData,
      pathIntent
    })
  }

  throw new Error(`No category slug passed to generateCreateUrlFromCategory on ${sourceKey}`)
}

/**
 * Given a path schema slug, create a url where they can create a new path from
 */
interface CreateFromPathSlugProps extends CreatePathProps {
  maybePathSlug?: string | null
}

export function generateCreateUrl({
  sourceKey,
  pathIntent,
  prefillData,
  maybePathSlug,
  onboardingAsDefault = true
}: CreateFromPathSlugProps) {
  const cleanedPrefillData = cleanPrefillData(prefillData)
  const prefillDataQueryString = cleanedPrefillData
    ? `?${queryString.stringify(
        cleanedPrefillData
      )}&sourceKey=${sourceKey}&pathIntent=${pathIntent}`
    : `?sourceKey=${sourceKey}&pathIntent=${pathIntent}`

  if (maybePathSlug) {
    return `/requests/create/${maybePathSlug}${prefillDataQueryString}`
  }

  if (onboardingAsDefault) {
    return `/requests/create/${getOnboardingPathSlug()}${prefillDataQueryString}`
  }

  throw new Error(`No path slug passed to generateCreateUrl on ${sourceKey}`)
}

/**
 * Get the first uncompleted page from the path segment
 */
export function getFirstUncompletedPage(pagesConnection: PathFlowPagesConnectionFragment) {
  const firstEdge = pagesConnection.edges.find(edge => !edge.node.completed)

  return firstEdge ? firstEdge.node : null
}

/**
 * Given a path flow segment, identify the next path page to move to
 */
function handleNextPathPage(flow: {
  id: string
  slug: string
  pagesConnection: PathFlowPagesConnectionFragment
}) {
  const firstPage = getFirstUncompletedPage(flow.pagesConnection)
  if (firstPage) {
    return `/requests/${flow.slug}/${firstPage.slug}?flowId=${flow.id}`
  } else {
    throw new Error(`No uncompleted pages for flow ${flow.id}!`)
  }
}

/**
 * Redirects a user with a lead created
 */
function handleOnCompleteLeadDestination({
  flow,
  conversation,
  location
}: {
  flow: PathFlowFragment
  conversation: PathFlowOnCompleteConversationWithLeadFragment
  location: PathFlowOnCompleteRedirectLocation
}) {
  switch (location) {
    case PathFlowOnCompleteRedirectLocation.EXPERT_PROFILE:
      if (conversation && conversation.lead && conversation.lead.expert.id) {
        return `/experts/${conversation.lead.expert.vanityId}?expert-introduction`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.EXPERT_PROFILE completed with incomplete data'
      )

    case PathFlowOnCompleteRedirectLocation.INBOX:
      return `/inbox/${conversation.id}?expert-introduction`

    case PathFlowOnCompleteRedirectLocation.DEPARTMENT_PAGE:
      const flowDepartment =
        flow.category && flow.category.department ? flow.category.department : null

      if (flowDepartment && flowDepartment.slug) {
        return `/d/${flowDepartment.slug}?expert-introduction`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.DEPARTMENT_PAGE completed with incomplete data'
      )

    case PathFlowOnCompleteRedirectLocation.CATEGORY:
      const flowCategorySlug = flow.category && flow.category.slug

      if (flowCategorySlug) {
        return `/c/${flowCategorySlug}?expert-introduction`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.CATEGORY completed with incomplete data'
      )

    /**
     * Added for a golf test, a phone collection test only
     */
    case PathFlowOnCompleteRedirectLocation.PHONE_COLLECTION_PATH:
      return '/requests/create/golf-phone-collection'

    default:
      return '/?expert-introduction'
  }
}

/**
 * Redirects a user without a lead created
 */
function handleOnCompleteRedirectOnly({
  flow,
  location,
  callback
}: {
  flow: PathFlowFragment
  location: PathFlowOnCompleteRedirectLocation
  callback?: () => void
}) {
  switch (location) {
    case PathFlowOnCompleteRedirectLocation.APPLY_FINISH:
      if (flow.onComplete && flow.onComplete.__typename === 'PathFlowOnCompleteExpertApplication') {
        return `/apply/create-profile/${flow.onComplete.application.id}`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.APPLY_FINISH completed with incomplete data'
      )

    case PathFlowOnCompleteRedirectLocation.LOGIN:
      return '/auth/login?header=WELCOME_BACK'

    case PathFlowOnCompleteRedirectLocation.RELOAD_CURRENT_PAGE:
      return callback

    case PathFlowOnCompleteRedirectLocation.DEPARTMENT_PAGE:
      const flowDepartment =
        flow.category && flow.category.department ? flow.category.department : null

      if (flowDepartment && flowDepartment.slug) {
        return `/d/${flowDepartment.slug}`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.DEPARTMENT_PAGE completed with incomplete data'
      )

    case PathFlowOnCompleteRedirectLocation.CATEGORY:
      const flowCategorySlug = flow.category && flow.category.slug

      if (flowCategorySlug) {
        return `/c/${flowCategorySlug}`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.CATEGORY completed with incomplete data'
      )

    default:
      return '/'
  }
}

/**
 * Redirects a user following an upsert
 */
function handleOnCompleteRedirectWithUpsert({
  flow,
  location
}: {
  flow: PathFlowFragment
  location: PathFlowOnCompleteRedirectLocation
}) {
  switch (location) {
    case PathFlowOnCompleteRedirectLocation.DEPARTMENT_PAGE:
      const flowDepartment =
        flow.category && flow.category.department ? flow.category.department : null

      if (flowDepartment && flowDepartment.slug) {
        return `/d/${flowDepartment.slug}?expert-introduction`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.DEPARTMENT_PAGE completed with incomplete data'
      )

    case PathFlowOnCompleteRedirectLocation.CATEGORY:
      const flowCategorySlug = flow.category && flow.category.slug

      if (flowCategorySlug) {
        return `/c/${flowCategorySlug}?expert-introduction`
      }

      Logger.captureException(
        'PathFlowOnCompleteRedirectLocation.CATEGORY completed with incomplete data'
      )

    default:
      return '/'
  }
}

/**
 * Given a location and conversation, handle where to send the user to next
 * This happens on completion of the entire path flow
 */
export function handleDestinationLocation(flow: PathFlowFragment, callback?: () => void) {
  if (flow.completed && flow.onComplete) {
    switch (flow.onComplete.__typename) {
      case 'PathFlowOnCompleteBroadcast':
      case 'PathFlowOnCompleteLead':
        return handleOnCompleteLeadDestination({
          flow: flow,
          conversation: flow.onComplete.conversation,
          location: flow.onComplete.location
        })

      case 'PathFlowOnCompleteRedirectWithUpsert':
        return handleOnCompleteRedirectWithUpsert({
          flow: flow,
          location: flow.onComplete.location
        })

      case 'PathFlowOnCompleteNewPath':
        return handleNextPathPage(flow.onComplete.flow)

      case 'PathFlowOnCompleteExpertReview':
      case 'PathFlowOnCompleteRedirectOnly':
      case 'PathFlowOnCompleteExpertApplication':
        return handleOnCompleteRedirectOnly({
          flow,
          location: flow.onComplete.location,
          callback
        })

      default:
        throw new Error(`Unknown onComplete enum value with [${flow.onComplete}]`)
    }
  } else {
    return handleNextPathPage(flow)
  }
}

/**
 * Return the metadata associated with an interstitial if it exists
 */
export function extractPageInterstitialData(metaData: PathFlowPageMetadataFragment[]) {
  const maybeInterstitial = metaData.find(
    data => data.__typename === 'InterstitialPathFlowPageMetadata'
  )

  return maybeInterstitial && maybeInterstitial.__typename === 'InterstitialPathFlowPageMetadata'
    ? maybeInterstitial
    : null
}

/**
 * Return the metadata data associated with a generic modal if it exists
 */
export function extractGenericModalMetadata(
  metaData: PathFlowPageMetadataFragment[]
): GenericModalPageMetadataFragment | null {
  const maybeGenericModal = metaData.find(data => data.__typename === 'GenericModalPageMetadata')

  return maybeGenericModal && maybeGenericModal.__typename === 'GenericModalPageMetadata'
    ? maybeGenericModal
    : null
}

/**
 * Return the previous node in the pages connection given the current page slug
 */
export function getPreviousNodeFromPage(pathFlow: PathFlowFragment, pageSlug: string) {
  const edges = pathFlow.pagesConnection.edges
  const currentIndex = edges.findIndex(edge => edge.node.slug === pageSlug)
  const prevNode = edges[currentIndex - 1] ? edges[currentIndex - 1].node : ''

  return prevNode
}

/**
 * Return the most recent node in the pages connection
 */
export function getLatestNode(pathFlow: PathFlowFragment) {
  const latestNode = pathFlow.pagesConnection.edges.slice(-1)

  return latestNode[0].node
}

export function getInputUnit(unit: NumericPathFlowQuestionUnitType) {
  switch (unit) {
    case NumericPathFlowQuestionUnitType.INCHES:
      return 'in'

    case NumericPathFlowQuestionUnitType.POUNDS:
      return 'lbs'

    case NumericPathFlowQuestionUnitType.KILOGRAMS:
      return 'kg'

    case NumericPathFlowQuestionUnitType.CENTIMETERS:
      return 'cm'

    case NumericPathFlowQuestionUnitType.FEET:
      return 'ft'

    case NumericPathFlowQuestionUnitType.DOLLARS:
      return '$'

    default:
      return ''
  }
}
